/* global tempoServerVals */
import * as d3 from 'd3'

const { currentSeason, initialDetailedData, leagueAverageData, params } =
    tempoServerVals
const tau = 2 * Math.PI
const angleDelta = tau / 60

const totalWidth = 240
const marginX = 5

const rectHeight = 440 // can this be dynamic?
const rectWidth = totalWidth - 2 * marginX

const height = 120
const width = 120

const r = Math.min(width / 2, height / 2)
const strokeWidth = 5
const outerRadius = r + 35
const clockHandWidth = 5

const averageColor = '#c2c2c2'
// const oldAverageColor = 'rgb(162, 49, 42)'

const { season_start, season_end, splitYears, entity_code } = params

const emptyAvg = leagueAverageData.empty_median_seconds
const runnerOnAvg = leagueAverageData.runner_on_median_seconds

const svgClockViewBox = 200
const nativeSvgClockWidth = 124
const desiredClockWidth = 2 * r + strokeWidth
const svgClockImageWidth =
    (svgClockViewBox * desiredClockWidth) / nativeSvgClockWidth

function fetchData(
    playerId,
    startYear = currentSeason,
    endYear = currentSeason,
    playerType = 'Pit'
) {
    const gameType = tempoServerVals.params.game_type
    return fetch(
        `/leaderboard/pitch-tempo-player?entity_id=${playerId}&season_start=${startYear}&season_end=${endYear}&type=${playerType}&game_type=${gameType}`
    ).then((res) => res.json())
}

const svg = d3.select('#pitch-tempo-viz svg')
// .style('background', '#ebf7f7')

const marginTop = 70

svg.append('text')
    .text('Bases Empty')
    .attr('transform', `translate(50, ${marginTop + height / 2}) rotate(-90)`)
    .attr('text-anchor', 'middle')
    .attr('font-size', 18)
    .attr('font-weight', 500)

svg.append('text')
    .text('Runner On Base')
    .attr(
        'transform',
        `translate(50, ${marginTop + height / 2 + 220}) rotate(-90)`
    )
    .attr('text-anchor', 'middle')
    .attr('font-size', 18)
    .attr('font-weight', 500)

function setupBackground() {
    const initialMargin = 50
    // Setup SVG
    const previousCharts = document.querySelectorAll('#pitch-tempo-viz svg > g')
    const len = previousCharts.length

    let g

    if (len > 3) {
        g = svg
            .append('g')
            .attr(
                'transform',
                `translate(${len * totalWidth + initialMargin}, 2)`
            )

        previousCharts.forEach((el, i) => {
            if (i === 0) {
                el.remove()
                return
            }

            d3.select(el)
                .transition()
                .attr(
                    'transform',
                    `translate(${(i - 1) * totalWidth + initialMargin}, 2)`
                )
        })

        g.transition().attr(
            'transform',
            `translate(${(len - 1) * totalWidth + initialMargin}, 2)`
        )
    } else {
        g = svg
            .append('g')
            .attr(
                'transform',
                `translate(${len * totalWidth + initialMargin}, 2)`
            )
    }

    g.append('rect')
        .attr('width', rectWidth)
        .attr('height', rectHeight)
        .attr('fill', '#ebf7f7')
        .attr('stroke', '#666')
        .attr('stroke-width', 3)
        .attr('rx', '5')
        .attr('y', '30')
        .attr('opacity', 0)

    g.append('rect')
        .attr('width', rectWidth)
        .attr('height', 2)
        .attr('fill', '#666')
        .attr('y', rectHeight / 2 + 25)
        .attr('opacity', 0)

    return g
}

if (initialDetailedData.length) {
    initialDetailedData.forEach((detailedData) => {
        // This should always be true
        if (detailedData.length) {
            const g = setupBackground()
            buildChart(g, detailedData)
        }
    })
}

function updateChart(playerId, year) {
    d3.select('#instruction-text').transition().duration(400).attr('opacity', 0)

    const g = setupBackground()

    const seasonStart = splitYears ? year : season_start
    const seasonEnd = splitYears ? year : season_end

    fetchData(playerId, seasonStart, seasonEnd, entity_code).then(
        (detailedData) => buildChart(g, detailedData)
    )
}

function buildChart(g, detailedData) {
    const baseStates = d3.group(detailedData, (d) => d.key_runners_code)

    // Bases Empty
    const basesEmpty = baseStates.get('Bases Empty')
    const runnerOn = baseStates.get('Runner On Base')

    const { median_seconds: emptyMedianSeconds } =
        basesEmpty?.find((d) => d.median_seconds) || {}
    const { median_seconds: runnerOnMedianSeconds } =
        runnerOn?.find((d) => d.median_seconds) || {}

    const entityName = basesEmpty[0]
        ? basesEmpty[0].entity_name
        : runnerOn[0].entity_name

    const basesEmptyG = g.append('g').attr('transform', 'translate(0, 50)')
    const runnerOnG = g.append('g').attr('transform', 'translate(0, 270)')

    g.append('text')
        .text(entityName)
        .attr('x', rectWidth / 2)
        .attr('y', 20) // nudge
        .attr('text-anchor', 'middle')
        .attr('font-family', 'sans-serif')
        .attr('font-weight', 'bold')
        .attr('font-size', 20)

    if (basesEmpty) {
        setup(basesEmptyG)
        updateViz(
            basesEmptyG,
            wrangleData(basesEmpty),
            emptyMedianSeconds,
            emptyAvg
        )
    }
    if (runnerOn) {
        setup(runnerOnG)

        updateViz(
            runnerOnG,
            wrangleData(runnerOn),
            runnerOnMedianSeconds,
            runnerOnAvg
        )
    }
}

function setup(el) {
    const wrapper = el
        .append('g')
        .attr('transform', `translate(0, 20)`)
        .attr('class', 'wrapper')

    setupClock(wrapper)

    wrapper
        .append('g')
        .attr('class', 'sector-wrapper')
        .attr('transform', `translate(${rectWidth / 2}, ${height / 2})`)

    wrapper
        .append('g')
        .attr('transform', `translate(${rectWidth / 2}, ${width / 2})`)
        .attr('class', 'pitcher-hand')
        .append('rect')
        .attr('transform', `rotate(270)`)
        .attr('fill', 'rgb(25, 35, 65)')
        .attr('x', 0)
        .attr('rx', 3)
        .attr('y', clockHandWidth / -2)
        .attr('height', clockHandWidth)
        // .attr('width', outerRadius + handOverHang)
        .attr('width', r - strokeWidth - 4) // fudge

    wrapper
        .append('g')
        .attr('transform', `translate(${rectWidth / 2}, ${width / 2})`)
        .attr('class', 'average-hand-inner')
        .append('rect')
        .attr('transform', `rotate(-90)`)
        .attr('fill', averageColor)
        .attr('x', 0)
        .attr('rx', 3)
        .attr('y', clockHandWidth / -2)
        .attr('height', clockHandWidth)
        .attr('width', r - strokeWidth - 4) // fudge

    const boxHeight = 35
    const boxWidth = 60
    const boxStroke = 2
    const boxBorderRadius = boxWidth / 8

    // Median
    const medianBox = wrapper
        .append('g')
        .attr('transform', `translate(${rectWidth / 2}, ${height / 2})`)
        .attr('class', 'median-box')

    medianBox
        .append('rect')
        .attr('fill', 'rgb(25, 35, 65)')
        .attr('stroke', 'rgb(255, 255, 255)')
        .attr('stroke-width', boxStroke)
        .attr('width', boxWidth)
        .attr('height', boxHeight)
        .attr('x', boxWidth / -2)
        .attr('y', boxHeight / -2)
        .attr('rx', boxBorderRadius)

    // bottom box size
    const leagueAverageBoxHeight = (boxHeight * 5) / 12
    medianBox
        .append('rect')
        .attr('fill', averageColor)
        .attr('stroke', 'rgb(255, 255, 255)')
        .attr('stroke-width', boxStroke)
        .attr('width', boxWidth)
        .attr('height', leagueAverageBoxHeight + boxStroke / 2)
        .attr('x', boxWidth / -2)
        .attr('y', boxHeight / 2 - leagueAverageBoxHeight)
        .attr('rx', boxBorderRadius)

    medianBox
        .append('rect')
        .attr('fill', averageColor)
        .attr('width', boxWidth - boxStroke)
        .attr('height', leagueAverageBoxHeight - boxBorderRadius / 2)
        .attr('x', boxWidth / -2 + boxStroke / 2)
        .attr('y', boxHeight / 2 - leagueAverageBoxHeight - boxStroke / 2)

    medianBox
        .append('text')
        .attr('class', 'pitcher-median')
        .attr('y', -6) // I feel like it needs a nudge
        .attr('fill', 'white')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-family', 'sans-serif')
        .attr('font-weight', '500')
        .attr('font-size', 16)

    medianBox
        .append('text')
        .attr('class', 'league-median')
        .attr('y', 10) // I feel like it needs a nudge
        .attr('fill', 'black')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-family', 'sans-serif')
        .attr('font-size', 8)
        .attr('font-weight', 500)
}

function setupClock(wrapper) {
    wrapper
        .append('image')
        .attr('href', '/sections/leaderboard/images/clock-b.svg')
        .attr('y', -55)
        .attr('x', 14)
        .attr('width', Math.round(svgClockImageWidth)) // fudged... with math
        .attr('height', Math.round(svgClockImageWidth))

    // wrapper
    //     .append('circle')
    //     .attr('r', r)
    //     .attr('cx', rectWidth / 2)
    //     .attr('cy', height / 2)
    //     .attr('stroke', '#555')
    //     .attr('stroke-width', strokeWidth)
    //     .attr('fill', '#fff')

    // function tickWidth(d) {
    //     return d % 15 === 0 ? 4 : 1
    // }

    // function tickHeight(d) {
    //     return d % 15 === 0 ? 13 : 7
    // }

    // const ticks = d3.range(0, 60, 5)
    // wrapper
    //     .append('g')
    //     .attr('class', 'tick-group')
    //     .attr('transform', `translate(${rectWidth / 2}, ${height / 2})`)
    //     .selectAll('rect.tick')
    //     .data(ticks)
    //     .join((enter) =>
    //         enter
    //             .append('rect')
    //             .attr('class', 'tick')
    //             .attr('fill', '#555')
    //             .attr(
    //                 'transform',
    //                 (d) =>
    //                     `
    //             rotate(${(360 * d) / 60 - 90})
    //             translate(${r - tickHeight(d) - strokeWidth / 2}, ${
    //                         tickWidth(d) * -1
    //                     })
    //             `
    //             )
    //             .attr('x', 0)
    //             .attr('y', (d) => tickWidth(d) / 2)
    //             .attr('height', tickWidth)
    //             .attr('width', tickHeight)
    //     )
}

function strip60Plus(data) {
    let n = 0
    let tot = 0

    const max = 50

    const { entity_name } = data[0]
    data.forEach((d) => {
        if (d.elapsed_seconds >= max) {
            n += d.n
        }
        tot += d.n
    })

    const filtered = data.filter((d) => d.elapsed_seconds < max)

    if (n > 0) {
        filtered.push({
            entity_name,
            elapsed_seconds: max,
            n,
            freq_pitches: n / tot,
        })
    }

    return filtered
}

function sectorize(data, fill = false) {
    let parsedData = data
    if (fill) {
        const [secondsMin, secondsMax] = d3.extent(
            data,
            (d) => d.elapsed_seconds
        )

        const range = d3.range(secondsMin, secondsMax + 1)
        // super naive
        parsedData = range.map((sec) => {
            const datum = data.find((d) => d.elapsed_seconds === sec)
            return datum || { elapsed_seconds: sec, n: 0 }
        })
    }
    return parsedData.map((d) => {
        const startAngle = (d.elapsed_seconds / 60) * tau - angleDelta / 2
        const endAngle = startAngle + angleDelta
        return {
            ...d,
            startAngle,
            endAngle,
        }
    })
}

function wrangleData(data) {
    const totalPitches = data.reduce((prev, curr) => prev + curr.n, 0)
    const cutoff = 0.01 // percent of pitches to show up

    const removeSmallBucketsData = data.filter(
        (d) => d.n > totalPitches * cutoff
    )

    const dataStripped = strip60Plus(removeSmallBucketsData).sort((a, b) =>
        a.median_seconds > b.median_seconds ? 1 : -1
    )
    return sectorize(dataStripped, true)
}

function updateViz(g, data, medianSeconds, leagueAverage) {
    // Update
    const maxFreq = Math.max(
        d3.max(data, (d) => d.freq_pitches),
        0.15
    )

    const radiusScale = d3
        .scaleLinear()
        .domain([0, maxFreq])
        .range([r + 10, outerRadius])

    const arcGenerator = d3
        .arc()
        .innerRadius(r + (strokeWidth * 3) / 4)
        .outerRadius((d) => radiusScale(d.freq_pitches))
        .padAngle(0.01)
        .padRadius(50)
        .cornerRadius(2)

    const colorScale = d3
        .scaleLinear()
        // .range(['#88f', '#ff7777'])
        // .domain([30, 15])
        .range(['#4444c0', '#88f', '#ff7777', '#f14545'])
        .domain([60, 35, 20, 10])

        .clamp(true)

    // Arcs
    g.select('g.sector-wrapper')
        .selectAll('path')
        .data(data, (d) => d.elapsed_seconds)
        .join(
            (enter) =>
                enter
                    .append('path')
                    .attr('class', (d) => `seconds-${d.elapsed_seconds}`)
                    .attr('d', arcGenerator)
                    .attr('fill', (d) => colorScale(d.elapsed_seconds)),
            (update) => {
                update
                    .transition()
                    .attr('fill', (d) => colorScale(d.elapsed_seconds))
                return update
            },
            (exit) => exit.remove()
        )

    const animationDivisor = 10
    // Clock hand
    g.select('g.pitcher-hand rect')
        // Divide transition into two, so it doesn't go the wrong way.
        .transition()
        .duration((medianSeconds * 1000) / 2 / animationDivisor)
        .ease(d3.easeLinear)
        .attr('transform', `rotate(${(360 * medianSeconds) / 2 / 60 + 270})`)
        .transition()
        .duration((medianSeconds * 1000) / 2 / animationDivisor)
        .ease(d3.easeLinear)
        .attr('transform', `rotate(${(360 * medianSeconds) / 60 + 270})`)

    g.selectAll('g.average-hand-inner rect')
        .transition()
        .duration((leagueAverage * 1000) / animationDivisor)
        .ease(d3.easeLinear)
        .attr('transform', `rotate(${(360 * leagueAverage) / 60 - 90})`)

    // Median
    g.select('g.median-box text.pitcher-median')
        .transition()
        .duration((medianSeconds * 1000) / animationDivisor)
        .ease(d3.easeLinear)
        .textTween(() => (i) => {
            const lerp = d3.interpolate(0, medianSeconds)
            return `${lerp(i).toFixed(1)} s`
        })

    g.select('g.median-box text.league-median').text(
        `LG AVG: ${leagueAverage.toFixed(1)}`
    )
    // .attr('opacity', 0)
    // .transition()
    // .delay((leagueAverage * 1000) / animationDivisor)
    // .attr('opacity', 1)
}

document.addEventListener('click', ({ target }) => {
    if (target && target.classList.contains('compare-btn')) {
        const { playerId, year } = target.dataset
        updateChart(playerId, year)
    }
})
